import React, { useState, useEffect } from 'react';

import { Grid } from '@mui/material';
import { supportedReportFilters } from '../../utils/variables';
import SPDSelector from '../../Components/SPDSelector';
import ChapterSelector from '../../Components/ChapterSelector';
import DisbandedFilter from '../../Components/DisbandedFilter';
import MemberActiveFilter from '../../Components/MemberActiveFilter';
import PresetDateFilter from '../../Components/PresetDateFilter';
import DateFilter from '../../Components/DateFilter';
import ChapterStatusFilter from '../../Components/ChapterStatusFilter';

interface FiltersProps {
  filters: Record<string, any>;
  updateFilters: (filters: Record<string, any>) => void;
  reportId: string;
  disabledFilters: string[];
}

interface SupportedFiltersProps {
  filterKey: string;
  filterValue: any;
  updateFilters: (filterKey: string, value: any) => void;
  dependantKey: any;
  reportId: string;
  disabledFilters: string[];
}

function SupportedFilters({
  filterKey,
  filterValue,
  updateFilters,
  dependantKey,
  reportId,
  disabledFilters,
}: SupportedFiltersProps) {
  switch (filterKey) {
    case 'spd_id':
      return (
        <SPDSelector
          spd={filterValue}
          reportId={reportId}
          handleSpdSelect={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'chapter_id':
      return (
        <ChapterSelector
          chapter={filterValue}
          spd={dependantKey}
          reportId={reportId}
          handleChapterSelect={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'disbanded':
      return (
        <DisbandedFilter
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'member_status':
      return (
        <MemberActiveFilter
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'date_selector':
      return (
        <PresetDateFilter
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'start_date':
      return (
        <DateFilter
          label={'Start Date'}
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'end_date':
      return (
        <DateFilter
          label={'End Date'}
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'inactive_start_date':
      return (
        <DateFilter
          label={'Inactive Start Date'}
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    case 'chapter_status':
      return (
        <ChapterStatusFilter
          disabled={disabledFilters.includes(filterKey)}
          filterValue={filterValue}
          updateFilters={(value: any) => updateFilters(filterKey, value)}
        />
      );
    default:
      return null;
  }
}

export default function Filters({
  filters,
  updateFilters,
  reportId,
  disabledFilters,
}: FiltersProps) {
  const [xsmallGrid, setXSmallGrid] = useState<number>(12);
  const [smallGrid, setSmallGrid] = useState<number>(6);
  const [mediumGrid, setMediumGrid] = useState<number>(4);
  const [largeGrid, setLargeGrid] = useState<number>(3);

  useEffect(() => {
    const filterCount = Object.keys(filters).length;
    if (filterCount === 1) {
      setXSmallGrid(12);
      setSmallGrid(12);
      setMediumGrid(12);
      setLargeGrid(12);
    } else if (filterCount === 2) {
      setXSmallGrid(12);
      setSmallGrid(6);
      setMediumGrid(6);
      setLargeGrid(6);
    } else {
      setXSmallGrid(12);
      setSmallGrid(6);
      setMediumGrid(4);
      setLargeGrid(4);
    }
  }, [filters]);

  // need to order the filters based on the order in supportedReportFilters list
  const orderedFilters = Object.keys(filters)
    .sort((a, b) => {
      const indexA = supportedReportFilters.indexOf(a);
      const indexB = supportedReportFilters.indexOf(b);
      return indexA - indexB;
    })
    .reduce((obj: Record<string, any>, key: string) => {
      obj[key] = filters[key];
      return obj;
    }, {});

  const handleUpdateFilter = (filterKey: string, filterValue: any) => {
    // Create new filters object with the updated value
    let newFilters = { ...filters };
    newFilters[filterKey] = filterValue;

    // Handle dependent filter logic locally if needed
    if (
      filterKey === 'spd_id' &&
      filterValue === null &&
      Object.keys(filters).includes('chapter_id')
    ) {
      // Update the chapter_id filter as well
      newFilters['chapter_id'] = null;
    }

    // Call the parent's updateFilters with the complete filters object
    updateFilters(newFilters);
  };

  return (
    <Grid container>
      {Object.keys(orderedFilters).map((filterKey, index) => (
        <Grid
          item
          xs={xsmallGrid}
          sm={smallGrid}
          md={mediumGrid}
          lg={largeGrid}
          key={index}
          sx={{ p: 1 }}
        >
          <SupportedFilters
            disabledFilters={disabledFilters}
            filterKey={filterKey}
            filterValue={filters[filterKey]}
            reportId={reportId}
            updateFilters={handleUpdateFilter}
            dependantKey={
              filterKey === 'chapter_id' &&
              Object.keys(filters).includes('spd_id')
                ? filters['spd_id']
                : null
            }
          />
        </Grid>
      ))}
    </Grid>
  );
}
